import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineUserGroup,
  HiOutlineUser,
  HiOutlinePlus,
  HiOutlineSearch,
} from 'react-icons/hi';
import { getTeamMembers } from '../services/api';
import './TeamMembers.css';

const SERVICE_TYPE_LABELS = {
  cloud_run: 'Cloud Run',
  vm_instance: 'VM',
  cloud_run_job: 'Job',
  cloud_task: 'Task',
  cloud_build: 'Build',
  cloud_scheduler: 'Scheduler',
  bucket: 'Bucket',
  service_account: 'SA',
  gemini_api: 'Gemini',
  pub_sub: 'Pub/Sub',
};

function TeamMembers({ onAddMember, teamMembers, onRefresh }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return teamMembers;
    const q = search.toLowerCase();
    return teamMembers.filter(m =>
      m.name.toLowerCase().includes(q) ||
      m.services?.some(s => s.name.toLowerCase().includes(q))
    );
  }, [teamMembers, search]);

  const getServiceNames = (member) => {
    if (!member.services?.length) return [];
    return member.services.map(s => s.name);
  };

  const getTypeSummary = (member) => {
    if (!member.services?.length) return {};
    const counts = {};
    for (const svc of member.services) {
      counts[svc.type] = (counts[svc.type] || 0) + 1;
    }
    return counts;
  };

  return (
    <div className="team-list-page">
      <div className="team-list-header">
        <div className="team-list-header-left">
          <HiOutlineUserGroup size={24} />
          <h1>Team Members</h1>
          <span className="team-list-count">{teamMembers.length}</span>
        </div>
        <div className="team-list-header-right">
          <div className="header-search">
            <HiOutlineSearch size={16} />
            <input
              type="text"
              placeholder="Search members or services..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={onAddMember}>
            <HiOutlinePlus size={16} /> Add Member
          </button>
        </div>
      </div>

      {filtered.length === 0 && teamMembers.length === 0 && (
        <div className="team-list-empty">
          <HiOutlineUserGroup size={48} />
          <h3>No team members yet</h3>
          <p>Add team members to track who manages which GCP services.</p>
          <button className="btn btn-primary" onClick={onAddMember}>
            <HiOutlinePlus size={16} /> Add First Member
          </button>
        </div>
      )}

      {filtered.length === 0 && teamMembers.length > 0 && (
        <div className="team-list-empty">
          <p>No members match "{search}"</p>
        </div>
      )}

      <div className="team-list-grid">
        {filtered.map(member => {
          const serviceNames = getServiceNames(member);
          const maxShow = 3;
          const shown = serviceNames.slice(0, maxShow);
          const remaining = serviceNames.length - maxShow;

          return (
            <div
              key={member._id}
              className="team-member-card"
              onClick={() => navigate(`/team/${member._id}`)}
            >
              <div className="team-member-card-top">
                <div className="team-member-card-info">
                  <div className="team-member-card-avatar">
                    <HiOutlineUser size={22} />
                  </div>
                  <div>
                    <h3>{member.name}</h3>
                  </div>
                </div>
                <span className="team-member-card-badge">
                  {member.services?.length || 0} service{(member.services?.length || 0) !== 1 ? 's' : ''}
                </span>
              </div>

              {serviceNames.length > 0 && (
                <div className="team-member-card-services">
                  {shown.map((name, idx) => (
                    <span key={idx} className="team-member-svc-chip">
                      <span className="team-member-svc-dot"></span>
                      {name}
                    </span>
                  ))}
                  {remaining > 0 && (
                    <span className="team-member-svc-more">+{remaining} more</span>
                  )}
                </div>
              )}

              <div className="team-member-card-footer">
                <span className="team-member-card-date">
                  Created {new Date(member.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TeamMembers;
